/**
 * eİcraPro kimliğinin (token + büro + TCKN) imzacıya devri — saf mantık.
 * Store/DOM/ağ bağımlılığı yok; ağ işi `baglam.devret` ile dışarıdan gelir.
 *
 * Neden ayrı: devrin "ne zaman tekrarlanır / ne zaman birleşir / ne zaman atlanır"
 * kuralları (DEV-3899) store'dan bağımsız denenebilsin.
 */

export type KimlikDevriNedeni = 'giris' | 'buro' | 'socket' | 'imzaci-acildi' | 'kullanici' | 'sayfa';

export const KIMLIK_DEVRI_NEDENLERI: KimlikDevriNedeni[] = ['giris', 'buro', 'socket', 'imzaci-acildi', 'kullanici', 'sayfa'];

/** Bu süre içinde aynı kimlikle gelen (muaf olmayan) tetikleyiciler önceki sonucu paylaşır. */
export const BIRLESTIRME_MS = 3000;
/** 'sayfa' tetikleyicisi: son başarılı devir bu süreden yeniyse hiç istek atılmaz. */
export const SAYFA_ATLAMA_MS = 10 * 60 * 1000;
/** Devir bu süreyi aşarsa başarısız sayılır; sonraki tetikleyici uçuşta takılı kalmaz. */
export const DEVIR_ZAMAN_ASIMI_MS = 20000;

export interface KimlikDevriSonucu {
    ok: boolean;
    eskiKabuk: boolean;
    kabukSurumu?: string;
    /** 'eksik' | 'reddedildi' | 'ulasilamadi' | 'ag' | 'zamanasimi' (ok:false iken) */
    sebep?: string;
    /** Kabuk reddettiyse HTTP kodu. */
    durum?: number;
    /** Hiç istek atılmadı: 'sayfa' tetikleyicisi yumuşak atlandı. */
    atlandi?: boolean;
}

export interface KimlikDevriBaglami {
    parmakIzi: () => string | null;
    socketBagli: () => boolean;
    devret: () => Promise<any>;
}

export function nedenGecerliMi(x: any): boolean {
    return KIMLIK_DEVRI_NEDENLERI.some((neden) => neden === x);
}

/** Bilinen neden kendisi; bilinmeyen / eksik neden kullanıcı eylemi sayılır ('kullanici': birleşmeden muaf). */
export function nedenSec(x: any): KimlikDevriNedeni {
    return KIMLIK_DEVRI_NEDENLERI.find((neden) => neden === x) || 'kullanici';
}

export function tcknGecerliMi(deger: any): boolean {
    return /^\d{11}$/.test(String(deger == null ? '' : deger));
}

/**
 * Kimlik parmak izi: token|büro|TCKN (TCKN 11 hane değilse boş). Yalnız "kimlik
 * değişti mi" karşılaştırması içindir: ASLA loglanmaz, Vuex state'ine konmaz.
 */
export function parmakIziOlustur(token: any, tenantId: any, kullanici: any): string | null {
    if (!token || !tenantId || !kullanici || !kullanici.id) return null;
    const tckn = tcknGecerliMi(kullanici.kimlik_no) ? String(kullanici.kimlik_no) : '';
    return String(token) + '|' + String(tenantId) + '|' + tckn;
}

/**
 * setApiCookiesToImza dönüşünü tek şekle çevirir. İki plugin sürümü de desteklenir:
 *  - eski: `true` döner ya da fırlatır;
 *  - yeni: {ok:true,eskiKabuk,kabukSurumu?} / {ok:false,sebep:'eksik'} döner.
 */
export function devirCevabiniNormallestir(cevap: any): KimlikDevriSonucu {
    if (cevap === true) return {ok: true, eskiKabuk: false};
    if (cevap && typeof cevap === 'object') {
        if (cevap.ok === true) {
            const sonuc: KimlikDevriSonucu = {ok: true, eskiKabuk: cevap.eskiKabuk === true};
            if (typeof cevap.kabukSurumu === 'string' && cevap.kabukSurumu) sonuc.kabukSurumu = cevap.kabukSurumu;
            return sonuc;
        }
        return {ok: false, eskiKabuk: false, sebep: typeof cevap.sebep === 'string' && cevap.sebep ? cevap.sebep : 'ag'};
    }
    return {ok: false, eskiKabuk: false, sebep: 'ag'};
}

/**
 * Yeni plugin gerçek başarısızlıkta Error.sebep = 'reddedildi' (HTTP cevabı var,
 * Error.durum = kod) ya da 'ulasilamadi' ile fırlatır; eski plugin sebepsiz bir hata
 * fırlatır ('ag').
 */
export function devirHatasiniNormallestir(hata: any): KimlikDevriSonucu {
    const sebep = hata && (hata.sebep === 'reddedildi' || hata.sebep === 'ulasilamadi') ? hata.sebep : 'ag';
    const sonuc: KimlikDevriSonucu = {ok: false, eskiKabuk: false, sebep};
    if (hata && typeof hata.durum === 'number') sonuc.durum = hata.durum;
    return sonuc;
}

export class KimlikDevriYoneticisi {
    ucustaki: Promise<KimlikDevriSonucu> | null = null;
    ucustakiIzi: string | null = null;
    sonBitis: number = 0;
    sonIzi: string | null = null;
    sonSonuc: KimlikDevriSonucu | null = null;
    sonBasari: { zaman: number, izi: string, sonuc: KimlikDevriSonucu } | null = null;
    simdi: () => number;

    constructor(simdi?: () => number) {
        this.simdi = simdi || (() => Date.now());
    }

    /** Socket koptu: önceki devrin sonucu artık geçerli sayılmaz (kabuk durumunu yitirmiş olabilir). */
    birlesmeyiSifirla(): void {
        this.sonBitis = 0;
        this.sonIzi = null;
        this.sonSonuc = null;
        this.sonBasari = null;
    }

    calistir(neden: KimlikDevriNedeni, baglam: KimlikDevriBaglami): Promise<KimlikDevriSonucu> {
        const izi = baglam.parmakIzi();
        // Token / büro / kullanıcı henüz yok: ne ağ ne plugin çağrısı (getTenantList de tetiklenmez).
        if (izi === null) {
            return Promise.resolve({ok: false, eskiKabuk: false, sebep: 'eksik'});
        }

        // 'sayfa' yumuşak tetikleyicidir: aynı kimlikle yakın zamanda başarılı devir
        // yapıldıysa ve canlı bağlantı sürüyorsa istek atılmaz.
        if (neden === 'sayfa' && this.sonBasari !== null && this.sonBasari.izi === izi
            && this.simdi() - this.sonBasari.zaman < SAYFA_ATLAMA_MS && baglam.socketBagli()) {
            return Promise.resolve(Object.assign({}, this.sonBasari.sonuc, {atlandi: true}));
        }

        if (this.ucustaki !== null) {
            if (this.ucustakiIzi === izi) return this.ucustaki;
            // Kimlik uçuş sırasında değişti: bitince yeni kimlikle bir kez daha devredilir.
            return this.ucustaki.then(() => this.calistir(neden, baglam));
        }

        // Bitişten hemen sonra gelen aynı kimlikli tetikleyiciler birleşir. İmzacı yeni açıldıysa
        // ('imzaci-acildi') ya da kullanıcı düğmeye bastıysa ('kullanici') birleşme YOK: imzacı
        // updateToken'ı kaçırmış olabilir, kullanıcı gerçek bir deneme bekler.
        const birlesebilir = neden !== 'imzaci-acildi' && neden !== 'kullanici';
        if (birlesebilir && this.sonSonuc !== null && this.sonIzi === izi && this.simdi() - this.sonBitis < BIRLESTIRME_MS) {
            return Promise.resolve(this.sonSonuc);
        }

        this.ucustakiIzi = izi;
        this.ucustaki = this.devret(baglam).then((sonuc) => {
            this.sonBitis = this.simdi();
            this.sonIzi = izi;
            this.sonSonuc = sonuc;
            this.sonBasari = sonuc.ok ? {zaman: this.sonBitis, izi: izi, sonuc: sonuc} : null;
            this.ucustaki = null;
            this.ucustakiIzi = null;
            return sonuc;
        });
        return this.ucustaki;
    }

    /** Asla reddetmez: hata ve zaman aşımı `ok:false` sonucuna çevrilir. */
    devret(baglam: KimlikDevriBaglami): Promise<KimlikDevriSonucu> {
        return new Promise<KimlikDevriSonucu>((coz) => {
            let bitti = false;
            const bitir = (sonuc: KimlikDevriSonucu) => {
                if (bitti) return;
                bitti = true;
                clearTimeout(zamanAsimi);
                coz(sonuc);
            };
            const zamanAsimi = setTimeout(() => bitir({ok: false, eskiKabuk: false, sebep: 'zamanasimi'}), DEVIR_ZAMAN_ASIMI_MS);
            let cevap: Promise<any>;
            try {
                cevap = Promise.resolve(baglam.devret());
            } catch (e) {
                cevap = Promise.reject(e);
            }
            cevap.then((c) => bitir(devirCevabiniNormallestir(c)), (e) => bitir(devirHatasiniNormallestir(e)));
        });
    }
}
